// Authentication Service
// Handles all authentication-related API calls
import { api, handleApiError, handleApiSuccess } from './api.js';
import { API_CONFIG, getAuthEndpointUrl, getOtpEndpointUrl, getV1EndpointUrl, getEndpointUrl } from '../config/api.js';
import { getLocalStorage } from '../utils/browserCheck.js';
// Small helper to sanitize objects before logging to avoid leaking secrets
const SENSITIVE_KEYS = [/password/i, /passw/i, /pwd/i, /secret/i, /token/i, /accessToken/i, /refreshToken/i, /otp/i, /otpCode/i];
function sanitizeForLogging(obj) {
  if (obj == null || typeof obj !== 'object') return obj;
  try {
    const clone = Array.isArray(obj) ? [] : {};
    for (const [k, v] of Object.entries(obj)) {
      if (SENSITIVE_KEYS.some((rx) => rx.test(k))) {
        clone[k] = '[REDACTED]';
      } else if (typeof v === 'object' && v !== null) {
        clone[k] = sanitizeForLogging(v);
      } else {
        clone[k] = v;
      }
    }
    return clone;
  } catch (e) {
    return '[UNSANITIZABLE]';
  }
}
// Removed port-specific storage imports - using regular localStorage
// Authentication Service Class
class AuthService {
  // Register a new user
  async register(userData) {
    try {
      const response = await api.post(getAuthEndpointUrl(API_CONFIG.AUTH_ENDPOINTS.REGISTER), userData);
      return handleApiSuccess(response);
    } catch (error) {
      throw handleApiError(error);
    }
  }
  // Register with OTP verification
  async registerWithOtp(userData) {
    try {
      const response = await api.post(
        getAuthEndpointUrl(API_CONFIG.ENHANCED_AUTH_ENDPOINTS.REGISTER_WITH_OTP),
        userData
      );
      return handleApiSuccess(response);
    } catch (error) {
      throw handleApiError(error);
    }
  }
  // Login user — single-domain: this is now the one login call every role
  // goes through (LoginPage.jsx no longer branches to hospitalLogin/
  // fleetLogin by subdomain), so it also has to preserve the storage side
  // effects those dedicated calls used to provide via storeHospitalLoginData
  // (namely HOSPITAL_ID, genuinely read by hospitalService.js and a couple
  // of booking pages) rather than just token/user like before.
  async login(credentials) {
    try {
      const response = await api.post(getAuthEndpointUrl(API_CONFIG.AUTH_ENDPOINTS.LOGIN), credentials);
      const result = handleApiSuccess(response);

      // Store JWT token and user data
      if (result.data.token) {
        localStorage.setItem(API_CONFIG.STORAGE_KEYS.JWT_TOKEN, result.data.token);
      }
      if (result.data.refreshToken) {
        localStorage.setItem(API_CONFIG.STORAGE_KEYS.REFRESH_TOKEN, result.data.refreshToken);
      }
      if (result.data.user) {
        localStorage.setItem(API_CONFIG.STORAGE_KEYS.USER_DATA, JSON.stringify(result.data.user));

        // Extract hospital/operator ID from user data (HOSPITAL_USER accounts)
        const hospitalId = result.data.user.operator_id || result.data.user.hospital_id;
        if (hospitalId) {
          localStorage.setItem(API_CONFIG.STORAGE_KEYS.HOSPITAL_ID, String(hospitalId));
        }
      }

      // Store hospital data if provided (HOSPITAL_USER role) — same
      // fallback fields storeHospitalLoginData used, kept for consistency
      if (result.data.hospital) {
        const hospitalData = result.data.hospital;
        localStorage.setItem(API_CONFIG.STORAGE_KEYS.HOSPITAL_DATA, JSON.stringify(hospitalData));
        const hospitalId = hospitalData.hospital_id || hospitalData.id || hospitalData.hospitalId;
        if (hospitalId) {
          localStorage.setItem(API_CONFIG.STORAGE_KEYS.HOSPITAL_ID, String(hospitalId));
        }
      }

      return result;
    } catch (error) {
      throw handleApiError(error);
    }
  }
  // Login with OTP (2FA)
  async loginWithOtp(credentials) {
    try {
      const response = await api.post(
        getAuthEndpointUrl(API_CONFIG.ENHANCED_AUTH_ENDPOINTS.LOGIN_WITH_OTP),
        credentials
      );
      return handleApiSuccess(response);
    } catch (error) {
      throw handleApiError(error);
    }
  }
  // Complete OTP login
  async completeOtpLogin(email, otpCode) {
    try {
      const response = await api.post(
        getAuthEndpointUrl(API_CONFIG.ENHANCED_AUTH_ENDPOINTS.COMPLETE_OTP_LOGIN),
        { email, otpCode }
      );
      const result = handleApiSuccess(response);
      // Store user data in localStorage (tokens are in httpOnly cookies)
      if (result.data.user) {
        localStorage.setItem(API_CONFIG.STORAGE_KEYS.USER_DATA, JSON.stringify(result.data.user));
      }
      return result;
    } catch (error) {
      throw handleApiError(error);
    }
  }

  // ============================================================================
  // NEW BACKEND INTEGRATION METHODS
  // ============================================================================

  // Hospital Login (direct login without OTP) - Uses the v1/hospital/auth/login endpoint
  async hospitalLogin(credentials) {
    try {
      console.log('🏥 [authService] Hospital login request:', sanitizeForLogging(credentials));
      // Use v1 base URL for hospital endpoints
      const hospitalLoginUrl = getV1EndpointUrl(API_CONFIG.HOSPITAL_ENDPOINTS.LOGIN);
      console.log('🏥 [authService] Hospital login URL:', hospitalLoginUrl);
      const response = await api.post(hospitalLoginUrl, credentials);
      const result = handleApiSuccess(response);

      console.log('🏥 [authService] Hospital login response:', sanitizeForLogging(result));

      // Store all hospital login data using shared helper
      this.storeHospitalLoginData(result);

      return result;
    } catch (error) {
      console.error('❌ [authService] Hospital login error:', error);
      throw handleApiError(error);
    }
  }

  // Send Login OTP (new simplified flow)
  async sendLoginOtp(loginData) {
    try {
      console.log('📤 [authService] Sending login OTP request:', sanitizeForLogging(loginData));
      const response = await api.post(
        getAuthEndpointUrl(API_CONFIG.AUTH_ENDPOINTS.SEND_LOGIN_OTP),
        loginData
      );
      console.log('✅ [authService] Send login OTP response:', sanitizeForLogging(response));
      return handleApiSuccess(response);
    } catch (error) {
      console.error('❌ [authService] Send login OTP error:', sanitizeForLogging(error));
      console.error('❌ [authService] Error response:', sanitizeForLogging(error?.response));
      throw handleApiError(error);
    }
  }

  // Complete Login (new simplified flow)
  async completeLogin(loginData) {
    try {
      const response = await api.post(
        getAuthEndpointUrl(API_CONFIG.AUTH_ENDPOINTS.COMPLETE_LOGIN),
        loginData
      );
      const result = handleApiSuccess(response);

      console.log('🔐 Complete Login Result:', sanitizeForLogging(result));
      console.log('🔐 Result.data:', sanitizeForLogging(result.data));

      // Store JWT token and user data
      if (result.data?.token) {
        let token = result.data.token;

        // Handle case where token is an object instead of string
        if (typeof token === 'object' && token !== null) {
          console.warn('⚠️ Token is an object, extracting token string');
          console.log('⚠️ Token object:', typeof token === 'string' ? (token.length > 20 ? token.substring(0, 20) + '...' : token) : sanitizeForLogging(token));
          // Try to extract the actual token string from the object
          token = token.data || token.token || token.accessToken || token.jwt;

          // If still an object, we have a problem
          if (typeof token === 'object') {
            console.error('❌ Could not extract token string from object:', sanitizeForLogging(token));
            throw new Error('Invalid token format - token is nested too deeply');
          }
        }

        // Ensure token is a string
        if (typeof token !== 'string') {
          console.error('❌ Token is not a string:', typeof token, typeof token === 'string' ? (token.length > 20 ? token.substring(0, 20) + '...' : token) : sanitizeForLogging(token));
          throw new Error('Invalid token format received from server');
        }

        localStorage.setItem(API_CONFIG.STORAGE_KEYS.JWT_TOKEN, token);

        // Verify token was actually stored
        const storedToken = localStorage.getItem(API_CONFIG.STORAGE_KEYS.JWT_TOKEN);
        if (storedToken === token) {
          if (token.length > 20) {
            console.log('✅ JWT Token stored successfully:', token.substring(0, 20) + '...');
          } else {
            console.log('✅ JWT Token stored successfully:', token);
          }
        } else {
          console.error('❌ Token storage failed! Stored value:', storedToken);
          console.error('❌ Expected value:', token);
        }
      } else {
        console.log('❌ No JWT token in response');
        console.log('❌ Response structure:', JSON.stringify(sanitizeForLogging(result), null, 2));
      }

      if (result.data?.refreshToken) {
        localStorage.setItem(API_CONFIG.STORAGE_KEYS.REFRESH_TOKEN, result.data.refreshToken);
        console.log('✅ Refresh Token stored');
      }

      if (result.data?.user && !result.data?.requiresPasswordReset) {
        // Elevate city/state to root user object for easy access
        // Check nested operator inside user, or sibling operator in response
        const operatorData = result.data.user.operator || result.data.operator;
        if (operatorData?.city && !result.data.user.city) {
          result.data.user.city = operatorData.city;
        }
        if (operatorData?.state && !result.data.user.state) {
          result.data.user.state = operatorData.state;
        }
        localStorage.setItem(API_CONFIG.STORAGE_KEYS.USER_DATA, JSON.stringify(result.data.user));
        console.log('✅ User data stored:', sanitizeForLogging(result.data.user));
      }

      return result;
    } catch (error) {
      console.error('❌ Complete Login Error:', error);
      throw handleApiError(error);
    }
  }

  // Hospital Send Login OTP (new endpoint) - Sends OTP for hospital login
  async hospitalSendLoginOtp(credentials) {
    try {
      console.log('🏥 [authService] Hospital send login OTP request:', sanitizeForLogging(credentials));
      // Use v1 base URL for hospital endpoints
      const hospitalSendOtpUrl = getV1EndpointUrl(API_CONFIG.HOSPITAL_ENDPOINTS.SEND_LOGIN_OTP);
      console.log('🏥 [authService] Hospital send OTP URL:', hospitalSendOtpUrl);
      const response = await api.post(hospitalSendOtpUrl, credentials);
      const result = handleApiSuccess(response);

      console.log('🏥 [authService] Hospital send OTP response:', sanitizeForLogging(result));

      return result;
    } catch (error) {
      console.error('❌ [authService] Hospital send OTP error:', error);
      throw handleApiError(error);
    }
  }

  // Hospital Complete Login (uses hospital endpoint for OTP verification)
  async hospitalCompleteLogin(loginData) {
    try {
      console.log('🏥 [authService] Hospital complete login request:', sanitizeForLogging(loginData));
      // Use v1 base URL for hospital endpoints
      const hospitalCompleteLoginUrl = getV1EndpointUrl(API_CONFIG.HOSPITAL_ENDPOINTS.COMPLETE_LOGIN);
      console.log('🏥 [authService] Hospital complete login URL:', hospitalCompleteLoginUrl);
      // Use hospital endpoint for OTP completion - send contact, loginType, and otpCode (no password needed)
      const response = await api.post(hospitalCompleteLoginUrl, {
        contact: loginData.contact,
        loginType: loginData.loginType,
        otpCode: loginData.otpCode
      });
      const result = handleApiSuccess(response);

      console.log('🏥 [authService] Hospital complete login response:', sanitizeForLogging(result));

      // Store all hospital login data using shared helper
      this.storeHospitalLoginData(result);

      return result;
    } catch (error) {
      console.error('❌ [authService] Hospital complete login error:', error);
      throw handleApiError(error);
    }
  }

  // Remove development mode check since we're enforcing production behavior
  isDevelopmentMode() {
    return false;
  }

  // Remove bypass OTP method as it's no longer needed
  // getBypassOtpCode() method removed

  // Shared helper for storing hospital login response data
  storeHospitalLoginData(result) {
    // If first-time login password reset is required, do not store tokens or user in localStorage
    if (result.data?.requiresPasswordReset) {
      return;
    }

    // Handle new response structure: data.tokens.accessToken, data.tokens.refreshToken, data.user
    if (result.data?.tokens) {
      // Store access token
      if (result.data.tokens.accessToken) {
        localStorage.setItem(API_CONFIG.STORAGE_KEYS.JWT_TOKEN, result.data.tokens.accessToken);
        console.log('✅ Hospital Access Token stored');
      }

      // Store refresh token
      if (result.data.tokens.refreshToken) {
        localStorage.setItem(API_CONFIG.STORAGE_KEYS.REFRESH_TOKEN, result.data.tokens.refreshToken);
        console.log('✅ Hospital Refresh Token stored');
      }
    }

    // Store user data
    if (result.data?.user) {
      // Elevate city/state to root user object for easy access
      // Check nested operator inside user, or sibling operator in response
      const operatorData = result.data.user.operator || result.data.operator;
      if (operatorData?.city && !result.data.user.city) {
        result.data.user.operator.city = operatorData.city;
      }
      if (operatorData?.state && !result.data.user.state) {
        result.data.user.operator.state = operatorData.state;
      }
      localStorage.setItem(API_CONFIG.STORAGE_KEYS.USER_DATA, JSON.stringify(result.data.user));
      console.log('✅ Hospital User data stored:', result.data.user);

      // Extract hospital/operator ID from user data
      const hospitalId = result.data.user.operator_id || result.data.user.hospital_id;
      if (hospitalId) {
        localStorage.setItem(API_CONFIG.STORAGE_KEYS.HOSPITAL_ID, String(hospitalId));
        console.log('✅ Hospital/Operator ID stored:', hospitalId);
      }
    }

    // Store hospital data if provided separately (for backward compatibility)
    if (result.data?.hospital) {
      const hospitalData = result.data.hospital;
      localStorage.setItem(API_CONFIG.STORAGE_KEYS.HOSPITAL_DATA, JSON.stringify(hospitalData));
      console.log('✅ Hospital data stored in localStorage');

      // Store hospital_id separately for easy access during booking
      const hospitalId = hospitalData.hospital_id || hospitalData.id || hospitalData.hospitalId;

      if (hospitalId) {
        localStorage.setItem(API_CONFIG.STORAGE_KEYS.HOSPITAL_ID, String(hospitalId));
        console.log('✅ Hospital ID stored from hospital data:', hospitalId);
      }
    }
  }

  // Logout user
  async logout() {
    try {
      await api.post(getAuthEndpointUrl(API_CONFIG.AUTH_ENDPOINTS.LOGOUT));
    } catch (error) {
      console.warn('Logout API call failed:', sanitizeForLogging(error));
    } finally {
      this.clearAuth();
    }
  }

  // Hospital Logout (uses hospital-specific endpoint)
  async hospitalLogout() {
    try {
      console.log('🏥 [authService] Hospital logout request');
      const hospitalLogoutUrl = getV1EndpointUrl(API_CONFIG.HOSPITAL_ENDPOINTS.LOGOUT);
      console.log('🏥 [authService] Hospital logout URL:', hospitalLogoutUrl);
      await api.post(hospitalLogoutUrl);
      console.log('✅ [authService] Hospital logout successful');
    } catch (error) {
      console.warn('❌ [authService] Hospital logout API call failed:', sanitizeForLogging(error));
    } finally {
      this.clearAuth();
    }
  }

  // Hospital Get Profile (uses hospital-specific endpoint)
  async hospitalGetProfile() {
    try {
      console.log('🏥 [authService] Hospital get profile request');
      const hospitalProfileUrl = getV1EndpointUrl(API_CONFIG.HOSPITAL_ENDPOINTS.PROFILE);
      console.log('🏥 [authService] Hospital profile URL:', hospitalProfileUrl);
      const response = await api.get(hospitalProfileUrl);
      console.log('✅ [authService] Hospital profile response:', sanitizeForLogging(response));
      return handleApiSuccess(response);
    } catch (error) {
      console.error('❌ [authService] Hospital get profile error:', error);
      throw handleApiError(error);
    }
  }

  // Hospital Update Profile (uses hospital-specific endpoint)
  async hospitalUpdateProfile(profileData) {
    try {
      console.log('🏥 [authService] Hospital update profile request:', sanitizeForLogging(profileData));
      const hospitalUpdateProfileUrl = getV1EndpointUrl(API_CONFIG.HOSPITAL_ENDPOINTS.UPDATE_PROFILE);
      console.log('🏥 [authService] Hospital update profile URL:', hospitalUpdateProfileUrl);
      const response = await api.put(hospitalUpdateProfileUrl, profileData);
      console.log('✅ [authService] Hospital update profile response:', sanitizeForLogging(response));
      const result = handleApiSuccess(response);

      // Update stored user data if profile update was successful
      if (result.data && result.data.user_id) {
        const currentUser = this.getUser();
        if (currentUser) {
          const updatedUser = {
            ...currentUser,
            name: result.data.name || currentUser.name,
            email: result.data.email || currentUser.email,
            phone: result.data.phone || currentUser.phone,
            user_id: result.data.user_id || currentUser.user_id
          };
          localStorage.setItem(API_CONFIG.STORAGE_KEYS.USER_DATA, JSON.stringify(updatedUser));
          console.log('✅ [authService] Updated user data in localStorage');
        }
      }

      return result;
    } catch (error) {
      console.error('❌ [authService] Hospital update profile error:', error);
      // Create an error object that preserves validation errors
      const apiError = handleApiError(error);
      const thrownError = new Error(apiError.message);
      // Attach additional error properties
      thrownError.errors = apiError.errors;
      thrownError.type = apiError.type;
      thrownError.response = apiError.response;
      thrownError.status = apiError.status;
      throw thrownError;
    }
  }

  // Hospital Change Password (uses hospital-specific endpoint)
  async hospitalChangePassword(passwordData) {
    try {
      console.log('🏥 [authService] Hospital change password request');
      const hospitalChangePasswordUrl = getV1EndpointUrl(API_CONFIG.HOSPITAL_ENDPOINTS.CHANGE_PASSWORD);
      console.log('🏥 [authService] Hospital change password URL:', hospitalChangePasswordUrl);
      const response = await api.put(hospitalChangePasswordUrl, passwordData);
      console.log('✅ [authService] Hospital change password response:', response);
      return handleApiSuccess(response);
    } catch (error) {
      console.error('❌ [authService] Hospital change password error:', error);
      throw handleApiError(error);
    }
  }

  // Hospital Refresh Token (uses hospital-specific endpoint)
  async hospitalRefreshToken(refreshToken) {
    try {
      console.log('🏥 [authService] Hospital refresh token request');
      const hospitalRefreshTokenUrl = getV1EndpointUrl(API_CONFIG.HOSPITAL_ENDPOINTS.REFRESH_TOKEN);
      console.log('🏥 [authService] Hospital refresh token URL:', hospitalRefreshTokenUrl);
      const response = await api.post(hospitalRefreshTokenUrl, { refreshToken });
      console.log('✅ [authService] Hospital refresh token response:', response);
      const result = handleApiSuccess(response);

      // Store new tokens if refresh was successful
      if (result.data && result.data.accessToken) {
        localStorage.setItem(API_CONFIG.STORAGE_KEYS.JWT_TOKEN, result.data.accessToken);
        console.log('✅ [authService] Hospital access token stored');
      }
      if (result.data && result.data.refreshToken) {
        localStorage.setItem(API_CONFIG.STORAGE_KEYS.REFRESH_TOKEN, result.data.refreshToken);
        console.log('✅ [authService] Hospital refresh token stored');
      }

      return result;
    } catch (error) {
      console.error('❌ [authService] Hospital refresh token error:', error);
      throw handleApiError(error);
    }
  }

  // Hospital Forgot Password (uses hospital-specific endpoint)
  async hospitalForgotPassword(contact) {
    try {
      console.log('🏥 [authService] Hospital forgot password request:', contact);
      const hospitalForgotPasswordUrl = getV1EndpointUrl(API_CONFIG.HOSPITAL_ENDPOINTS.FORGOT_PASSWORD);
      console.log('🏥 [authService] Hospital forgot password URL:', hospitalForgotPasswordUrl);
      const response = await api.post(hospitalForgotPasswordUrl, { contact });
      console.log('✅ [authService] Hospital forgot password response:', sanitizeForLogging(response));
      return handleApiSuccess(response);
    } catch (error) {
      console.error('❌ [authService] Hospital forgot password error:', error);
      throw handleApiError(error);
    }
  }

  // ============================================================================
  // FLEET AUTH METHODS (V1 ENDPOINTS)
  // ============================================================================

  // Fleet Login (POST /v1/fleet/auth/login)
  async fleetLogin(credentials) {
    try {
      console.log('🚛 [authService] Fleet login request:', sanitizeForLogging(credentials));
      const fleetLoginUrl = getV1EndpointUrl(API_CONFIG.FLEET_ENDPOINTS.LOGIN);
      console.log('🚛 [authService] Fleet login URL:', fleetLoginUrl);
      const response = await api.post(fleetLoginUrl, credentials);
      const result = handleApiSuccess(response);

      console.log('🚛 [authService] Fleet login response:', sanitizeForLogging(result));

      // Store all fleet login data using shared helper
      this.storeFleetLoginData(result);

      return result;
    } catch (error) {
      console.error('❌ [authService] Fleet login error:', error);
      throw handleApiError(error);
    }
  }

  // Fleet Send Login OTP (POST /v1/fleet/auth/send-login-otp)
  async fleetSendLoginOtp(loginData) {
    try {
      console.log('🚛 [authService] Fleet send login OTP request:', sanitizeForLogging(loginData));
      const fleetSendOtpUrl = getV1EndpointUrl(API_CONFIG.FLEET_ENDPOINTS.SEND_LOGIN_OTP);
      console.log('🚛 [authService] Fleet send OTP URL:', fleetSendOtpUrl);
      const response = await api.post(fleetSendOtpUrl, loginData);
      console.log('✅ [authService] Fleet send OTP response:', sanitizeForLogging(response));
      return handleApiSuccess(response);
    } catch (error) {
      console.error('❌ [authService] Fleet send login OTP error:', error);
      throw handleApiError(error);
    }
  }

  // Fleet Complete Login with OTP (POST /v1/fleet/auth/complete-login)
  async fleetCompleteLogin(loginData) {
    try {
      console.log('🚛 [authService] Fleet complete login request:', sanitizeForLogging(loginData));
      const fleetCompleteLoginUrl = getV1EndpointUrl(API_CONFIG.FLEET_ENDPOINTS.COMPLETE_LOGIN);
      console.log('🚛 [authService] Fleet complete login URL:', fleetCompleteLoginUrl);
      // Use fleet endpoint for OTP completion - send contact, loginType, and otpCode (no password needed)
      const response = await api.post(fleetCompleteLoginUrl, {
        contact: loginData.contact,
        loginType: loginData.loginType,
        otpCode: loginData.otpCode
      });
      const result = handleApiSuccess(response);
      console.log('🚛 [authService] Fleet complete login response:', sanitizeForLogging(result));

      // Store all fleet login data using shared helper
      this.storeFleetLoginData(result);

      return result;
    } catch (error) {
      console.error('❌ [authService] Fleet complete login error:', error);
      throw handleApiError(error);
    }
  }

  // Fleet Verify Reset Password OTP (POST /v1/fleet/auth/verify-reset-otp)
  async fleetVerifyResetOtp(email, otpCode) {
    try {
      console.log('🚛 [authService] Fleet verify reset OTP request:', { email });
      const fleetVerifyOtpUrl = getV1EndpointUrl(API_CONFIG.FLEET_ENDPOINTS.VERIFY_RESET_OTP);
      console.log('🚛 [authService] Fleet verify OTP URL:', fleetVerifyOtpUrl);
      const response = await api.post(fleetVerifyOtpUrl, { email, otpCode });
      console.log('✅ [authService] Fleet verify OTP response:', sanitizeForLogging(response));
      return handleApiSuccess(response);
    } catch (error) {
      console.error('❌ [authService] Fleet verify reset OTP error:', error);
      throw handleApiError(error);
    }
  }

  // Fleet Logout (POST /v1/fleet/auth/logout)
  async fleetLogout() {
    try {
      console.log('🚛 [authService] Fleet logout request');
      const fleetLogoutUrl = getV1EndpointUrl(API_CONFIG.FLEET_ENDPOINTS.LOGOUT);
      console.log('🚛 [authService] Fleet logout URL:', fleetLogoutUrl);
      await api.post(fleetLogoutUrl);
      console.log('✅ [authService] Fleet logout successful');
    } catch (error) {
      console.warn('❌ [authService] Fleet logout API call failed:', sanitizeForLogging(error));
    } finally {
      this.clearAuth();
    }
  }

  // Fleet Get Profile (GET /v1/fleet/auth/profile)
  async fleetGetProfile() {
    try {
      console.log('🚛 [authService] Fleet get profile request');
      const fleetProfileUrl = getV1EndpointUrl(API_CONFIG.FLEET_ENDPOINTS.GET_PROFILE);
      console.log('🚛 [authService] Fleet profile URL:', fleetProfileUrl);
      const response = await api.get(fleetProfileUrl);
      console.log('✅ [authService] Fleet profile response:', sanitizeForLogging(response));
      return handleApiSuccess(response);
    } catch (error) {
      console.error('❌ [authService] Fleet get profile error:', error);
      throw handleApiError(error);
    }
  }

  // Fleet Update Profile (PUT /v1/fleet/auth/update-profile)
  async fleetUpdateProfile(profileData) {
    try {
      console.log('🚛 [authService] Fleet update profile request:', sanitizeForLogging(profileData));
      const fleetUpdateProfileUrl = getV1EndpointUrl(API_CONFIG.FLEET_ENDPOINTS.UPDATE_PROFILE);
      console.log('🚛 [authService] Fleet update profile URL:', fleetUpdateProfileUrl);
      const response = await api.put(fleetUpdateProfileUrl, profileData);
      console.log('✅ [authService] Fleet update profile response:', sanitizeForLogging(response));
      const result = handleApiSuccess(response);

      // Update stored user data if profile update was successful
      if (result.data && result.data.operator_id) {
        const currentUser = this.getUser();
        if (currentUser) {
          const updatedUser = {
            ...currentUser,
            name: result.data.name || currentUser.name,
            contact_info: result.data.contact_info || currentUser.contact_info,
            emergency_contact: result.data.emergency_contact || currentUser.emergency_contact,
            address: result.data.address || currentUser.address,
            operator_id: result.data.operator_id || currentUser.operator_id
          };
          localStorage.setItem(API_CONFIG.STORAGE_KEYS.USER_DATA, JSON.stringify(updatedUser));
          console.log('✅ [authService] Updated fleet user data in localStorage');
        }
      }

      return result;
    } catch (error) {
      console.error('❌ [authService] Fleet update profile error:', error);
      // Create an error object that preserves validation errors
      const apiError = handleApiError(error);
      const thrownError = new Error(apiError.message);
      // Attach additional error properties
      thrownError.errors = apiError.errors;
      thrownError.type = apiError.type;
      thrownError.response = apiError.response;
      thrownError.status = apiError.status;
      throw thrownError;
    }
  }

  // Fleet Get Hospitals (GET /v1/fleet/{fleetId}/hospitals)
  async fleetGetHospitals(fleetId, params = {}) {
    try {
      console.log('🚛 [authService] Fleet get hospitals request:', { fleetId, params });
      const baseUrl = getV1EndpointUrl(API_CONFIG.FLEET_ENDPOINTS.GET_HOSPITALS);
      const fleetHospitalsUrl = `${baseUrl}/${fleetId}/hospitals`;
      console.log('🚛 [authService] Fleet hospitals URL:', fleetHospitalsUrl);
      const response = await api.get(fleetHospitalsUrl, { params });
      console.log('✅ [authService] Fleet hospitals response:', sanitizeForLogging(response));
      return handleApiSuccess(response);
    } catch (error) {
      console.error('❌ [authService] Fleet get hospitals error:', error);
      throw handleApiError(error);
    }
  }

  // Fleet Fetch Hospitals (GET /v1/fleet/fetch-hospitals) - For partnership hospital selection
  async fleetFetchHospitals() {
    try {
      console.log('🚛 [authService] Fleet fetch hospitals request');
      const fetchHospitalsUrl = getV1EndpointUrl(API_CONFIG.FLEET_ENDPOINTS.FETCH_HOSPITALS);
      console.log('🚛 [authService] Fleet fetch hospitals URL:', fetchHospitalsUrl);
      const response = await api.get(fetchHospitalsUrl);
      console.log('✅ [authService] Fleet fetch hospitals response:', sanitizeForLogging(response));
      return handleApiSuccess(response);
    } catch (error) {
      console.error('❌ [authService] Fleet fetch hospitals error:', error);
      throw handleApiError(error);
    }
  }

  // Fleet Get Partnerships (GET /v1/fleet/{fleetId}/partnerships)
  async fleetGetPartnerships(fleetId, params = {}) {
    try {
      console.log('🚛 [authService] Fleet get partnerships request:', { fleetId, params });
      const baseUrl = getV1EndpointUrl(API_CONFIG.FLEET_ENDPOINTS.GET_PARTNERSHIPS);
      const fleetPartnershipsUrl = `${baseUrl}/${fleetId}/partnerships`;
      console.log('🚛 [authService] Fleet partnerships URL:', fleetPartnershipsUrl);
      const response = await api.get(fleetPartnershipsUrl, { params });
      console.log('✅ [authService] Fleet partnerships response:', sanitizeForLogging(response));
      return handleApiSuccess(response);
    } catch (error) {
      console.error('❌ [authService] Fleet get partnerships error:', error);
      throw handleApiError(error);
    }
  }

  // Fleet Invite Hospital (POST /v1/fleet/{fleetId}/invite-hospital)
  async fleetInviteHospital(fleetId, inviteData) {
    try {
      console.log('🚛 [authService] Fleet invite hospital request:', { fleetId, inviteData });
      const baseUrl = getV1EndpointUrl(API_CONFIG.FLEET_ENDPOINTS.INVITE_HOSPITAL);
      const fleetInviteUrl = `${baseUrl}/${fleetId}/invite-hospital`;
      console.log('🚛 [authService] Fleet invite URL:', fleetInviteUrl);
      const response = await api.post(fleetInviteUrl, inviteData);
      console.log('✅ [authService] Fleet invite response:', sanitizeForLogging(response));
      return handleApiSuccess(response);
    } catch (error) {
      console.error('❌ [authService] Fleet invite hospital error:', error);
      throw handleApiError(error);
    }
  }

  // Fleet Get Partnership Requests (GET /v1/fleet/{fleetId}/partnership-requests)
  async fleetGetPartnershipRequests(fleetId, params = {}) {
    try {
      console.log('🚛 [authService] Fleet get partnership requests:', { fleetId, params });
      const baseUrl = getV1EndpointUrl(API_CONFIG.FLEET_ENDPOINTS.GET_PARTNERSHIP_REQUESTS);
      const requestsUrl = `${baseUrl}/${fleetId}/partnership-requests`;
      console.log('🚛 [authService] Fleet requests URL:', requestsUrl);
      const response = await api.get(requestsUrl, { params });
      console.log('✅ [authService] Fleet requests response:', sanitizeForLogging(response));
      return handleApiSuccess(response);
    } catch (error) {
      console.error('❌ [authService] Fleet get partnership requests error:', error);
      throw handleApiError(error);
    }
  }

  // Fleet Get Invitations (GET /v1/fleet/{fleetId}/invitations)
  async fleetGetInvitations(fleetId, params = {}) {
    try {
      console.log('🚛 [authService] Fleet get invitations:', { fleetId, params });
      const baseUrl = getV1EndpointUrl(API_CONFIG.FLEET_ENDPOINTS.GET_INVITATIONS);
      const invitationsUrl = `${baseUrl}/${fleetId}/invitations`;
      console.log('🚛 [authService] Fleet invitations URL:', invitationsUrl);
      const response = await api.get(invitationsUrl, { params });
      console.log('✅ [authService] Fleet invitations response:', sanitizeForLogging(response));
      return handleApiSuccess(response);
    } catch (error) {
      console.error('❌ [authService] Fleet get invitations error:', error);
      throw handleApiError(error);
    }
  }

  // Fleet Respond to Invitation (PUT /v1/fleet/{fleetId}/invitations/{requestId}/respond)
  async fleetRespondToInvitation(fleetId, requestId, responseData) {
    try {
      console.log('🚛 [authService] Fleet respond to invitation:', { fleetId, requestId, responseData });
      const baseUrl = getV1EndpointUrl(API_CONFIG.FLEET_ENDPOINTS.RESPOND_TO_INVITATION);
      const respondUrl = `${baseUrl}/${fleetId}/invitations/${requestId}/respond`;
      console.log('🚛 [authService] Fleet respond URL:', respondUrl);
      const response = await api.put(respondUrl, responseData);
      console.log('✅ [authService] Fleet respond response:', sanitizeForLogging(response));
      return handleApiSuccess(response);
    } catch (error) {
      console.error('❌ [authService] Fleet respond to invitation error:', error);
      throw handleApiError(error);
    }
  }

  // Fleet Update Partnership Request (PUT /v1/fleet/{fleetId}/partnership-requests/{requestId})
  async fleetUpdatePartnershipRequest(fleetId, requestId, updateData) {
    try {
      console.log('🚛 [authService] Fleet update partnership request:', { fleetId, requestId, updateData });
      const baseUrl = getV1EndpointUrl(API_CONFIG.FLEET_ENDPOINTS.UPDATE_PARTNERSHIP_REQUEST);
      const updateUrl = `${baseUrl}/${fleetId}/partnership-requests/${requestId}`;
      console.log('🚛 [authService] Fleet update URL:', updateUrl);
      const response = await api.put(updateUrl, updateData);
      console.log('✅ [authService] Fleet update response:', sanitizeForLogging(response));
      return handleApiSuccess(response);
    } catch (error) {
      console.error('❌ [authService] Fleet update partnership request error:', error);
      throw handleApiError(error);
    }
  }

  // Fleet Respond to Invitation (PUT /v1/fleet/{fleetId}/invitations/{requestId}/respond)
  async fleetRespondToInvitation(fleetId, requestId, responseData) {
    try {
      console.log('🚛 [authService] Fleet respond to invitation:', { fleetId, requestId, responseData });
      const baseUrl = getV1EndpointUrl(API_CONFIG.FLEET_ENDPOINTS.RESPOND_TO_INVITATION);
      const respondUrl = `${baseUrl}/${fleetId}/invitations/${requestId}/respond`;
      console.log('🚛 [authService] Fleet respond URL:', respondUrl);
      const response = await api.put(respondUrl, responseData);
      console.log('✅ [authService] Fleet respond response:', sanitizeForLogging(response));
      return handleApiSuccess(response);
    } catch (error) {
      console.error('❌ [authService] Fleet respond to invitation error:', error);
      throw handleApiError(error);
    }
  }

  // Fleet Delete Partnership Request (DELETE /v1/fleet/{fleetId}/partnership-requests/{requestId})
  async fleetDeletePartnershipRequest(fleetId, requestId) {
    try {
      console.log('🚛 [authService] Fleet delete partnership request:', { fleetId, requestId });
      const baseUrl = getV1EndpointUrl(API_CONFIG.FLEET_ENDPOINTS.DELETE_PARTNERSHIP_REQUEST);
      const deleteUrl = `${baseUrl}/${fleetId}/partnership-requests/${requestId}`;
      console.log('🚛 [authService] Fleet delete URL:', deleteUrl);
      const response = await api.delete(deleteUrl);
      console.log('✅ [authService] Fleet delete response:', sanitizeForLogging(response));
      return handleApiSuccess(response);
    } catch (error) {
      console.error('❌ [authService] Fleet delete partnership request error:', error);
      throw handleApiError(error);
    }
  }

  // Fleet Get Partnership Invitations (GET /v1/fleet/{fleetId}/invitations)
  async fleetGetPartnershipInvitations(fleetId, filters) {
    try {
      console.log('🚛 [authService] Fleet get partnership invitations:', { fleetId, filters });
      const baseUrl = getV1EndpointUrl(API_CONFIG.FLEET_ENDPOINTS.GET_INVITATIONS);
      const invitationsUrl = `${baseUrl}/${fleetId}/invitations`;
      console.log('🚛 [authService] Fleet invitations URL:', invitationsUrl);
      const response = await api.get(invitationsUrl, { params: filters });
      console.log('✅ [authService] Fleet invitations response:', sanitizeForLogging(response));
      return handleApiSuccess(response);
    } catch (error) {
      console.error('❌ [authService] Fleet get partnership invitations error:', error);
      throw handleApiError(error);
    }
  }

  // =========== FLEET ASSIGNMENT METHODS (v1 API) ===========

  // Fleet Get Pending Assignments (GET /v1/fleet/assignments)
  async fleetGetPendingAssignments(params = {}) {
    try {
      console.log('🚛 [authService] Fleet get pending assignments:', { params });
      const assignmentsUrl = getV1EndpointUrl(API_CONFIG.FLEET_ENDPOINTS.GET_ASSIGNMENTS);
      console.log('🚛 [authService] Fleet assignments URL:', assignmentsUrl);
      const response = await api.get(assignmentsUrl, { params });
      console.log('✅ [authService] Fleet assignments response:', sanitizeForLogging(response));
      return handleApiSuccess(response);
    } catch (error) {
      console.error('❌ [authService] Fleet get pending assignments error:', error);
      throw handleApiError(error);
    }
  }

  // Fleet Get Driver Response History (GET /v1/fleet/assignments/{bookingId}/responses)
  async fleetGetDriverResponseHistory(bookingId) {
    try {
      console.log('🚛 [authService] Fleet get driver response history:', { bookingId });
      const baseUrl = getV1EndpointUrl(API_CONFIG.FLEET_ENDPOINTS.GET_ASSIGNMENT_RESPONSES);
      const responseHistoryUrl = `${baseUrl}/${bookingId}/responses`;
      console.log('🚛 [authService] Fleet response history URL:', responseHistoryUrl);
      const response = await api.get(responseHistoryUrl);
      console.log('✅ [authService] Fleet response history response:', sanitizeForLogging(response));
      return handleApiSuccess(response);
    } catch (error) {
      console.error('❌ [authService] Fleet get driver response history error:', error);
      throw handleApiError(error);
    }
  }

  // Fleet Get Available Drivers (GET /v1/fleet/drivers/available/{ambulanceTypeId})
  async fleetGetAvailableDrivers(ambulanceTypeId, params = {}) {
    try {
      console.log('🚛 [authService] Fleet get available drivers:', { ambulanceTypeId, params });
      const baseUrl = getV1EndpointUrl(API_CONFIG.FLEET_ENDPOINTS.GET_AVAILABLE_DRIVERS);
      const availableDriversUrl = `${baseUrl}/${ambulanceTypeId}`;
      console.log('🚛 [authService] Fleet available drivers URL:', availableDriversUrl);
      const response = await api.get(availableDriversUrl, { params });
      console.log('✅ [authService] Fleet available drivers response:', sanitizeForLogging(response));
      return handleApiSuccess(response);
    } catch (error) {
      console.error('❌ [authService] Fleet get available drivers error:', error);
      throw handleApiError(error);
    }
  }

  // Fleet Vehicle Booking History (GET /v1/fleet/vehicles/:vehicle_id/history)
  async fleetGetVehicleHistory(vehicleId, params = {}) {
    try {
      console.log('🚛 [authService] Fleet get vehicle history:', { vehicleId, params });
      const baseUrl = getV1EndpointUrl(API_CONFIG.FLEET_ENDPOINTS.GET_VEHICLE_HISTORY);
      const vehicleHistoryUrl = `${baseUrl}/${vehicleId}/history`;
      console.log('🚛 [authService] Fleet vehicle history URL:', vehicleHistoryUrl);
      const response = await api.get(vehicleHistoryUrl, { params });
      console.log('✅ [authService] Fleet vehicle history response:', sanitizeForLogging(response));
      return handleApiSuccess(response);
    } catch (error) {
      console.error('❌ [authService] Fleet get vehicle history error:', error);
      throw handleApiError(error);
    }
  }

  // Fleet Assign Driver to Booking (POST /v1/fleet/assignments/{requestId}/assign)
  async fleetAssignDriverToBooking(requestId, assignmentData) {
    try {
      console.log('🚛 [authService] Fleet assign driver to booking:', { requestId, assignmentData: sanitizeForLogging(assignmentData) });
      const baseUrl = getV1EndpointUrl(API_CONFIG.FLEET_ENDPOINTS.ASSIGN_DRIVER);
      const assignDriverUrl = `${baseUrl}/${requestId}/assign`;
      console.log('🚛 [authService] Fleet assign driver URL:', assignDriverUrl);
      const response = await api.post(assignDriverUrl, assignmentData);
      console.log('✅ [authService] Fleet assign driver response:', sanitizeForLogging(response));
      return handleApiSuccess(response);
    } catch (error) {
      console.error('❌ [authService] Fleet assign driver to booking error:', error);
      throw handleApiError(error);
    }
  }

  // Fleet Reject Assignment (POST /v1/fleet/assignments/{requestId}/reject)
  async fleetRejectAssignment(requestId, rejectionData) {
    try {
      console.log('🚛 [authService] Fleet reject assignment:', { requestId, rejectionData: sanitizeForLogging(rejectionData) });
      const baseUrl = getV1EndpointUrl(API_CONFIG.FLEET_ENDPOINTS.REJECT_ASSIGNMENT);
      const rejectAssignmentUrl = `${baseUrl}/${requestId}/reject`;
      console.log('🚛 [authService] Fleet reject assignment URL:', rejectAssignmentUrl);
      const response = await api.post(rejectAssignmentUrl, rejectionData);
      console.log('✅ [authService] Fleet reject assignment response:', sanitizeForLogging(response));
      return handleApiSuccess(response);
    } catch (error) {
      console.error('❌ [authService] Fleet reject assignment error:', error);
      throw handleApiError(error);
    }
  }

  // =========== FLEET BOOKING METHODS (v1 API) ===========

  // Fleet Create Booking (POST /v1/fleet/bookings)
  async fleetCreateBooking(bookingData) {
    try {
      console.log('🚛 [authService] Fleet create booking (original):', sanitizeForLogging(bookingData));

      // Check authentication
      const token = localStorage.getItem(API_CONFIG.STORAGE_KEYS.JWT_TOKEN);
      console.log('🚛 [authService] Token available:', !!token);

      // Transform camelCase payload to snake_case format expected by backend
      const transformedPayload = {
        patient_name: bookingData.patientName,
        patient_phone: bookingData.patientPhone,
        patient_phone_country_code: bookingData.patientPhone_country_code || '+91',
        patient_email: bookingData.patientEmail || null,
        patient_age: bookingData.patientAge || 30,
        patient_gender: bookingData.patientGender || 'OTHER',
        pickup_address: bookingData.pickupLocation?.address,
        pickup_latitude: bookingData.pickupLocation?.latitude,
        pickup_longitude: bookingData.pickupLocation?.longitude,
        dropoff_address: bookingData.dropoffLocation?.address,
        dropoff_latitude: bookingData.dropoffLocation?.latitude,
        dropoff_longitude: bookingData.dropoffLocation?.longitude,
        booking_type: bookingData.bookingType || 'INSTANT',
        emergency_type: bookingData.emergencyType || 'NON_URGENT',
        priority_level: bookingData.priority || 'NORMAL',
        payment_method: bookingData.paymentMethod || 'CASH',
        ambulance_type: bookingData.ambulanceType || null,
        notes: bookingData.notes || '',
        journey_direction: bookingData.journeyDirection || 'OUTBOUND',
        estimated_fare: bookingData.estimatedFare || null,
        scheduled_datetime: bookingData.scheduledDateTime || null,
        // Include fleet partner preferences if available
        preferred_fleet_id: bookingData.preferredFleetId || null,
        fleet_partner_ids: bookingData.fleetPartnerIds || null,
        // Include selected hospital ID for fleet bookings
        hospital_id: bookingData.hospitalId || null
      };

      console.log('🚛 [authService] Fleet create booking (transformed):', sanitizeForLogging(transformedPayload));
      const createBookingUrl = getV1EndpointUrl(API_CONFIG.FLEET_ENDPOINTS.CREATE_BOOKING);
      console.log('🚛 [authService] Fleet create booking URL:', createBookingUrl);
      console.log('🚛 [authService] About to make request to:', createBookingUrl);
      console.log('🚛 [authService] Request payload:', sanitizeForLogging(transformedPayload));

      const response = await api.post(createBookingUrl, transformedPayload);
      console.log('✅ [authService] Fleet create booking response:', sanitizeForLogging(response));
      return handleApiSuccess(response);
    } catch (error) {
      console.error('❌ [authService] Fleet create booking error:', error);
      console.error('❌ [authService] Error details:', {
        message: error.message,
        code: error.code,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        config: {
          url: error.config?.url,
          method: error.config?.method,
          baseURL: error.config?.baseURL,
          timeout: error.config?.timeout
        }
      });
      throw handleApiError(error);
    }
  }

  // Fleet Get Bookings (GET /v1/fleet/bookings)
  async fleetGetBookings(params = {}) {
    try {
      console.log('🚛 [authService] Fleet get bookings request:', { params });
      const getBookingsUrl = getV1EndpointUrl(API_CONFIG.FLEET_ENDPOINTS.GET_BOOKINGS);
      console.log('🚛 [authService] Fleet get bookings URL:', getBookingsUrl);
      const response = await api.get(getBookingsUrl, { params });
      console.log('✅ [authService] Fleet get bookings response:', sanitizeForLogging(response));
      return handleApiSuccess(response);
    } catch (error) {
      console.error('❌ [authService] Fleet get bookings error:', error);
      throw handleApiError(error);
    }
  }

  // Fleet Get Booking Details (GET /v1/fleet/bookings/{bookingId})
  async fleetGetBookingDetails(bookingId) {
    try {
      console.log('🚛 [authService] Fleet get booking details:', { bookingId });
      const baseUrl = getV1EndpointUrl(API_CONFIG.FLEET_ENDPOINTS.GET_BOOKING_DETAILS);
      const bookingDetailsUrl = `${baseUrl}/${bookingId}`;
      console.log('🚛 [authService] Fleet booking details URL:', bookingDetailsUrl);
      const response = await api.get(bookingDetailsUrl);
      console.log('✅ [authService] Fleet booking details response:', sanitizeForLogging(response));
      return handleApiSuccess(response);
    } catch (error) {
      console.error('❌ [authService] Fleet get booking details error:', error);
      throw handleApiError(error);
    }
  }

  // Fleet Get Booking History (GET /v1/fleet/bookings/history)
  async fleetGetBookingHistory(params = {}) {
    try {
      console.log('🚛 [authService] Fleet get booking history request:', { params });
      const historyUrl = getV1EndpointUrl(API_CONFIG.FLEET_ENDPOINTS.GET_BOOKING_HISTORY);
      console.log('🚛 [authService] Fleet booking history URL:', historyUrl);
      const response = await api.get(historyUrl, { params });
      console.log('✅ [authService] Fleet booking history response:', sanitizeForLogging(response));
      return handleApiSuccess(response);
    } catch (error) {
      console.error('❌ [authService] Fleet get booking history error:', error);
      throw handleApiError(error);
    }
  }

  // Fleet Update Booking Status (PUT /v1/fleet/bookings/{bookingId}/status)
  async fleetUpdateBookingStatus(bookingId, statusData) {
    try {
      console.log('🚛 [authService] Fleet update booking status:', { bookingId, statusData });
      const baseUrl = getV1EndpointUrl(API_CONFIG.FLEET_ENDPOINTS.UPDATE_BOOKING_STATUS);
      const updateStatusUrl = `${baseUrl}/${bookingId}/status`;
      console.log('🚛 [authService] Fleet update status URL:', updateStatusUrl);
      const response = await api.put(updateStatusUrl, statusData);
      console.log('✅ [authService] Fleet update status response:', sanitizeForLogging(response));
      return handleApiSuccess(response);
    } catch (error) {
      console.error('❌ [authService] Fleet update booking status error:', error);
      throw handleApiError(error);
    }
  }

  // Fleet Cancel Booking (PUT /v1/fleet/bookings/{bookingId}/cancel)
  async fleetCancelBooking(bookingId, cancelData) {
    try {
      console.log('🚛 [authService] Fleet cancel booking:', { bookingId, cancelData });
      const baseUrl = getV1EndpointUrl(API_CONFIG.FLEET_ENDPOINTS.CANCEL_BOOKING);
      const cancelBookingUrl = `${baseUrl}/${bookingId}/cancel`;
      console.log('🚛 [authService] Fleet cancel booking URL:', cancelBookingUrl);
      const response = await api.put(cancelBookingUrl, cancelData);
      console.log('✅ [authService] Fleet cancel booking response:', sanitizeForLogging(response));
      return handleApiSuccess(response);
    } catch (error) {
      console.error('❌ [authService] Fleet cancel booking error:', error);
      throw handleApiError(error);
    }
  }

  // Fleet Get Booking Status History (GET /v1/fleet/bookings/{bookingId}/status-history)
  async fleetGetBookingStatusHistory(bookingId) {
    try {
      console.log('🚛 [authService] Fleet get booking status history:', { bookingId });
      const baseUrl = getV1EndpointUrl(API_CONFIG.FLEET_ENDPOINTS.GET_BOOKING_STATUS_HISTORY);
      const statusHistoryUrl = `${baseUrl}/${bookingId}/status-history`;
      console.log('🚛 [authService] Fleet status history URL:', statusHistoryUrl);
      const response = await api.get(statusHistoryUrl);
      console.log('✅ [authService] Fleet status history response:', sanitizeForLogging(response));
      return handleApiSuccess(response);
    } catch (error) {
      console.error('❌ [authService] Fleet get booking status history error:', error);
      throw handleApiError(error);
    }
  }

  // Fleet Get Booking Tracking (GET /v1/fleet/bookings/{bookingId}/tracking)
  async fleetGetBookingTracking(bookingId) {
    try {
      console.log('🚛 [authService] Fleet get booking tracking:', { bookingId });
      const baseUrl = getV1EndpointUrl(API_CONFIG.FLEET_ENDPOINTS.GET_BOOKING_TRACKING);
      const trackingUrl = `${baseUrl}/${bookingId}/tracking`;
      console.log('🚛 [authService] Fleet tracking URL:', trackingUrl);
      const response = await api.get(trackingUrl);
      console.log('✅ [authService] Fleet tracking response:', sanitizeForLogging(response));
      return handleApiSuccess(response);
    } catch (error) {
      console.error('❌ [authService] Fleet get booking tracking error:', error);
      throw handleApiError(error);
    }
  }

  // Fleet Get Booking Route (GET /v1/fleet/bookings/{bookingId}/tracking/route)
  // Returns historical route data for route playback in history screen
  async fleetGetBookingRoute(bookingId) {
    try {
      console.log('🚛 [authService] Fleet get booking route:', { bookingId });
      const baseUrl = getV1EndpointUrl(API_CONFIG.FLEET_ENDPOINTS.GET_BOOKING_TRACKING);
      const routeUrl = `${baseUrl}/${bookingId}/tracking/route`;
      console.log('🚛 [authService] Fleet route URL:', routeUrl);
      const response = await api.get(routeUrl);
      console.log('✅ [authService] Fleet route response:', sanitizeForLogging(response));
      return handleApiSuccess(response);
    } catch (error) {
      console.error('❌ [authService] Fleet get booking route error:', error);
      throw handleApiError(error);
    }
  }

  // Fleet Get Booking Tracking Events (GET /v1/fleet/bookings/{bookingId}/tracking/events)
  // Returns tracking events (status changes, driver actions) for history display
  async fleetGetBookingTrackingEvents(bookingId) {
    try {
      console.log('🚛 [authService] Fleet get booking tracking events:', { bookingId });
      const baseUrl = getV1EndpointUrl(API_CONFIG.FLEET_ENDPOINTS.GET_BOOKING_TRACKING);
      const eventsUrl = `${baseUrl}/${bookingId}/tracking/events`;
      console.log('🚛 [authService] Fleet tracking events URL:', eventsUrl);
      const response = await api.get(eventsUrl);
      console.log('✅ [authService] Fleet tracking events response:', sanitizeForLogging(response));
      return handleApiSuccess(response);
    } catch (error) {
      console.error('❌ [authService] Fleet get booking tracking events error:', error);
      throw handleApiError(error);
    }
  }

  // Fleet Estimate Booking Fare (POST /v1/fleet/bookings/estimate-fare)
  async fleetEstimateBookingFare(fareData) {
    try {
      console.log('🚛 [authService] Fleet estimate booking fare:', sanitizeForLogging(fareData));

      // Check authentication
      const token = localStorage.getItem(API_CONFIG.STORAGE_KEYS.JWT_TOKEN);
      console.log('🚛 [authService] Token available for fare estimate:', !!token);

      const estimateFareUrl = getV1EndpointUrl(API_CONFIG.FLEET_ENDPOINTS.ESTIMATE_BOOKING_FARE);
      console.log('🚛 [authService] Fleet estimate fare URL:', estimateFareUrl);
      console.log('🚛 [authService] About to make fare estimate request to:', estimateFareUrl);

      const response = await api.post(estimateFareUrl, fareData);
      console.log('✅ [authService] Fleet estimate fare response:', sanitizeForLogging(response));
      return handleApiSuccess(response);
    } catch (error) {
      console.error('❌ [authService] Fleet estimate booking fare error:', error);
      console.error('❌ [authService] Fare estimate error details:', {
        message: error.message,
        code: error.code,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        config: {
          url: error.config?.url,
          method: error.config?.method,
          baseURL: error.config?.baseURL,
          timeout: error.config?.timeout
        }
      });
      throw handleApiError(error);
    }
  }

  // Fleet Initiate Booking Payment (POST /v1/fleet/bookings/{bookingId}/initiate-payment)
  async fleetInitiateBookingPayment(bookingId, paymentData) {
    try {
      console.log('🚛 [authService] Fleet initiate booking payment:', { bookingId, paymentData: sanitizeForLogging(paymentData) });
      const baseUrl = getV1EndpointUrl(API_CONFIG.FLEET_ENDPOINTS.INITIATE_BOOKING_PAYMENT);
      const initiatePaymentUrl = `${baseUrl}/${bookingId}/initiate-payment`;
      console.log('🚛 [authService] Fleet initiate payment URL:', initiatePaymentUrl);
      const response = await api.post(initiatePaymentUrl, paymentData);
      console.log('✅ [authService] Fleet initiate payment response:', sanitizeForLogging(response));
      return handleApiSuccess(response);
    } catch (error) {
      console.error('❌ [authService] Fleet initiate booking payment error:', error);
      throw handleApiError(error);
    }
  }

  // Fleet Verify Payment (POST /v1/fleet/payments/verify)
  async fleetVerifyPayment(paymentData) {
    try {
      console.log('🚛 [authService] Fleet verify payment:', sanitizeForLogging(paymentData));
      const endpoint = getV1EndpointUrl(API_CONFIG.FLEET_ENDPOINTS.VERIFY_PAYMENT);
      console.log('🚛 [authService] Fleet verify payment URL:', endpoint);
      const response = await api.post(endpoint, paymentData);
      console.log('✅ [authService] Fleet verify payment response:', sanitizeForLogging(response));
      return handleApiSuccess(response);
    } catch (error) {
      console.error('❌ [authService] Fleet verify payment error:', error);
      throw handleApiError(error);
    }
  }

  // Fleet Smart Match Booking (POST /v1/fleet/bookings/smart-match)
  async fleetSmartMatchBooking(matchData) {
    try {
      console.log('🚛 [authService] Fleet smart match booking:', sanitizeForLogging(matchData));
      const smartMatchUrl = getV1EndpointUrl(API_CONFIG.FLEET_ENDPOINTS.SMART_MATCH_BOOKING);
      console.log('🚛 [authService] Fleet smart match URL:', smartMatchUrl);
      const response = await api.post(smartMatchUrl, matchData);
      console.log('✅ [authService] Fleet smart match response:', sanitizeForLogging(response));
      return handleApiSuccess(response);
    } catch (error) {
      console.error('❌ [authService] Fleet smart match booking error:', error);
      throw handleApiError(error);
    }
  }

  // Fleet Get Candidates (POST /v1/fleet/bookings/candidates)
  async fleetGetCandidates(candidatesData) {
    try {
      console.log('🚛 [authService] Fleet get candidates:', sanitizeForLogging(candidatesData));
      const candidatesUrl = getV1EndpointUrl(API_CONFIG.FLEET_ENDPOINTS.GET_CANDIDATES);
      console.log('🚛 [authService] Fleet candidates URL:', candidatesUrl);
      const response = await api.post(candidatesUrl, candidatesData);
      console.log('✅ [authService] Fleet candidates response:', sanitizeForLogging(response));
      return handleApiSuccess(response);
    } catch (error) {
      console.error('❌ [authService] Fleet get candidates error:', error);
      throw handleApiError(error);
    }
  }

  // Fleet Start Manual Allocation (POST /v1/fleet/bookings/manual-allocation/start)
  async fleetStartManualAllocation(bookingPayload) {
    try {
      console.log('🚛 [authService] Fleet start manual allocation:', sanitizeForLogging(bookingPayload));
      const endpoint = getV1EndpointUrl(API_CONFIG.FLEET_ENDPOINTS.START_MANUAL_ALLOCATION);
      const payload = {
        booking_payload: bookingPayload
      };
      console.log('🚛 [authService] Fleet manual allocation URL:', endpoint);
      console.log('🚛 [authService] Fleet manual allocation payload:', sanitizeForLogging(payload));
      const response = await api.post(endpoint, payload);
      console.log('✅ [authService] Fleet manual allocation response:', sanitizeForLogging(response));
      return handleApiSuccess(response);
    } catch (error) {
      console.error('❌ [authService] Fleet start manual allocation error:', error);
      throw handleApiError(error);
    }
  }

  // Fleet Start Auto Allocation (POST /v1/fleet/bookings/auto-allocation/start)
  async fleetStartAutoAllocation(bookingPayload) {
    try {
      console.log('🚛 [authService] Fleet start auto allocation:', sanitizeForLogging(bookingPayload));
      const endpoint = getV1EndpointUrl(API_CONFIG.FLEET_ENDPOINTS.START_AUTO_ALLOCATION);
      const payload = {
        booking_payload: bookingPayload
      };
      console.log('🚛 [authService] Fleet auto allocation URL:', endpoint);
      console.log('🚛 [authService] Fleet auto allocation payload:', sanitizeForLogging(payload));
      const response = await api.post(endpoint, payload);
      console.log('✅ [authService] Fleet auto allocation response:', sanitizeForLogging(response));
      return handleApiSuccess(response);
    } catch (error) {
      console.error('❌ [authService] Fleet start auto allocation error:', error);
      throw handleApiError(error);
    }
  }

  // Fleet Trigger Next Driver (POST /v1/fleet/bookings/auto-allocation/{bookingId}/next)
  async fleetTriggerNextDriver(bookingId) {
    try {
      // Validate bookingId
      if (!bookingId) {
        throw new Error('Booking ID is required to trigger next driver');
      }

      console.log('🚛 [authService] Fleet trigger next driver:', { bookingId });
      const baseUrl = getV1EndpointUrl(API_CONFIG.FLEET_ENDPOINTS.TRIGGER_NEXT_DRIVER);
      const triggerNextDriverUrl = `${baseUrl}/${bookingId}/next`;
      console.log('🚛 [authService] Fleet trigger next driver URL:', triggerNextDriverUrl);

      // Make API call with explicit timeout handling
      // POST request with empty body (as per API spec)
      const response = await api.post(triggerNextDriverUrl, {}, {
        timeout: 60000, // 60 seconds timeout (longer than default 30s for this operation)
        validateStatus: (status) => status < 500, // Don't throw on 4xx errors, only 5xx
      });

      // Check if response indicates an error
      if (response.status >= 400) {
        const errorMessage = response.data?.message || response.data?.error || `HTTP ${response.status} error`;
        console.error('❌ [authService] Fleet trigger next driver API error:', {
          status: response.status,
          message: errorMessage,
          data: response.data
        });
        throw new Error(errorMessage);
      }

      console.log('✅ [authService] Fleet trigger next driver response:', sanitizeForLogging(response));
      return handleApiSuccess(response);
    } catch (error) {
      console.error('❌ [authService] Fleet trigger next driver error:', error);

      // Provide more specific error messages
      if (error.code === 'ECONNABORTED' || error.message?.toLowerCase().includes('timeout')) {
        throw new Error('Request timeout - the server took too long to respond. Please try again.');
      } else if (error.response?.status === 500) {
        const errorMessage = error.response?.data?.message || error.response?.data?.error || 'Internal server error';
        throw new Error(errorMessage);
      } else if (error.response?.status === 404) {
        throw new Error('Booking not found - the booking ID may be invalid or the booking may have been cancelled.');
      } else if (error.response?.status === 400) {
        const errorMessage = error.response?.data?.message || error.response?.data?.error || 'Invalid request';
        throw new Error(errorMessage);
      }

      throw handleApiError(error);
    }
  }

  // Fleet Get Allocation Status (GET /v1/fleet/bookings/{bookingId}/allocation-status)
  async fleetGetAllocationStatus(bookingId) {
    try {
      console.log('🚛 [authService] Fleet get allocation status:', { bookingId });
      const baseUrl = getV1EndpointUrl(API_CONFIG.FLEET_ENDPOINTS.GET_ALLOCATION_STATUS);
      const allocationStatusUrl = `${baseUrl}/${bookingId}/allocation-status`;
      console.log('🚛 [authService] Fleet allocation status URL:', allocationStatusUrl);
      const response = await api.get(allocationStatusUrl);
      console.log('✅ [authService] Fleet allocation status response:', sanitizeForLogging(response));
      return handleApiSuccess(response);
    } catch (error) {
      console.error('❌ [authService] Fleet get allocation status error:', error);
      throw handleApiError(error);
    }
  }

  // Fleet Stop Allocation (POST /v1/fleet/bookings/{bookingId}/stop-allocation)
  async fleetStopAllocation(bookingId) {
    try {
      console.log('🚛 [authService] Fleet stop allocation:', { bookingId });
      const baseUrl = getV1EndpointUrl(API_CONFIG.FLEET_ENDPOINTS.STOP_ALLOCATION);
      const stopAllocationUrl = `${baseUrl}/${bookingId}/stop-allocation`;
      console.log('🚛 [authService] Fleet stop allocation URL:', stopAllocationUrl);
      const response = await api.post(stopAllocationUrl);
      console.log('✅ [authService] Fleet stop allocation response:', sanitizeForLogging(response));
      return handleApiSuccess(response);
    } catch (error) {
      console.error('❌ [authService] Fleet stop allocation error:', error);
      throw handleApiError(error);
    }
  }

  // Fleet Get Payment Status (GET /v1/fleet/payments/{bookingId}/status)
  async fleetGetPaymentStatus(bookingId) {
    try {
      console.log('🚛 [authService] Fleet get payment status:', { bookingId });
      const baseUrl = getV1EndpointUrl(API_CONFIG.FLEET_ENDPOINTS.GET_PAYMENT_STATUS);
      const paymentStatusUrl = `${baseUrl}/${bookingId}/status`;
      console.log('🚛 [authService] Fleet payment status URL:', paymentStatusUrl);
      const response = await api.get(paymentStatusUrl);
      console.log('✅ [authService] Fleet payment status response:', sanitizeForLogging(response));
      return handleApiSuccess(response);
    } catch (error) {
      console.error('❌ [authService] Fleet get payment status error:', error);
      throw handleApiError(error);
    }
  }

  // Fleet Get Payment Attempts (GET /v1/fleet/payments/{bookingId}/attempts)
  async fleetGetPaymentAttempts(bookingId) {
    try {
      console.log('🚛 [authService] Fleet get payment attempts:', { bookingId });
      const baseUrl = getV1EndpointUrl(API_CONFIG.FLEET_ENDPOINTS.GET_PAYMENT_ATTEMPTS);
      const paymentAttemptsUrl = `${baseUrl}/${bookingId}/attempts`;
      console.log('🚛 [authService] Fleet payment attempts URL:', paymentAttemptsUrl);
      const response = await api.get(paymentAttemptsUrl);
      console.log('✅ [authService] Fleet payment attempts response:', sanitizeForLogging(response));
      return handleApiSuccess(response);
    } catch (error) {
      console.error('❌ [authService] Fleet get payment attempts error:', error);
      throw handleApiError(error);
    }
  }

  // Fleet Register (POST /v1/fleet/auth/register)
  async fleetRegister(registrationData) {
    try {
      console.log('🚛 [authService] Fleet register request:', sanitizeForLogging(registrationData));
      const fleetRegisterUrl = getV1EndpointUrl('/v1/fleet/auth/register');
      console.log('🚛 [authService] Fleet register URL:', fleetRegisterUrl);
      const response = await api.post(fleetRegisterUrl, registrationData);
      console.log('✅ [authService] Fleet register response:', sanitizeForLogging(response));
      const result = handleApiSuccess(response);

      // Store tokens if registration includes login
      if (result.data && result.data.accessToken) {
        localStorage.setItem(API_CONFIG.STORAGE_KEYS.JWT_TOKEN, result.data.accessToken);
        console.log('✅ [authService] Fleet access token stored after registration');

        if (result.data.refreshToken) {
          localStorage.setItem(API_CONFIG.STORAGE_KEYS.REFRESH_TOKEN, result.data.refreshToken);
          console.log('✅ [authService] Fleet refresh token stored after registration');
        }

        if (result.data.user) {
          localStorage.setItem(API_CONFIG.STORAGE_KEYS.USER_DATA, JSON.stringify(result.data.user));
          console.log('✅ [authService] Fleet user data stored after registration');
        }
      }

      return result;
    } catch (error) {
      console.error('❌ [authService] Fleet register error:', error);
      throw handleApiError(error);
    }
  }

  // Fleet Forgot Password (POST /v1/fleet/auth/forgot-password)
  async fleetForgotPassword(email) {
    try {
      console.log('🚛 [authService] Fleet forgot password request:', { email });
      const fleetForgotPasswordUrl = getV1EndpointUrl(API_CONFIG.FLEET_ENDPOINTS.FORGOT_PASSWORD);
      console.log('🚛 [authService] Fleet forgot password URL:', fleetForgotPasswordUrl);
      const response = await api.post(fleetForgotPasswordUrl, { email });
      console.log('✅ [authService] Fleet forgot password response:', sanitizeForLogging(response));
      return handleApiSuccess(response);
    } catch (error) {
      console.error('❌ [authService] Fleet forgot password error:', error);
      throw handleApiError(error);
    }
  }

  // Fleet Refresh Token (POST /v1/fleet/auth/refresh-token)
  async fleetRefreshToken(refreshToken) {
    try {
      console.log('🚛 [authService] Fleet refresh token request');
      const fleetRefreshTokenUrl = getV1EndpointUrl(API_CONFIG.FLEET_ENDPOINTS.REFRESH_TOKEN);
      console.log('🚛 [authService] Fleet refresh token URL:', fleetRefreshTokenUrl);
      const response = await api.post(fleetRefreshTokenUrl, { refreshToken });
      console.log('✅ [authService] Fleet refresh token response:', response);
      const result = handleApiSuccess(response);

      // Store new tokens if refresh was successful
      if (result.data && result.data.accessToken) {
        localStorage.setItem(API_CONFIG.STORAGE_KEYS.JWT_TOKEN, result.data.accessToken);
        console.log('✅ [authService] Fleet access token stored');
      }
      if (result.data && result.data.refreshToken) {
        localStorage.setItem(API_CONFIG.STORAGE_KEYS.REFRESH_TOKEN, result.data.refreshToken);
        console.log('✅ [authService] Fleet refresh token stored');
      }

      return result;
    } catch (error) {
      console.error('❌ [authService] Fleet refresh token error:', error);
      throw handleApiError(error);
    }
  }

  // Fleet Forgot Password (POST /v1/fleet/auth/forgot-password)
  async fleetForgotPassword(email) {
    try {
      console.log('🚛 [authService] Fleet forgot password request:', email);
      const fleetForgotPasswordUrl = getV1EndpointUrl(API_CONFIG.FLEET_ENDPOINTS.FORGOT_PASSWORD);
      console.log('🚛 [authService] Fleet forgot password URL:', fleetForgotPasswordUrl);
      const response = await api.post(fleetForgotPasswordUrl, { email });
      console.log('✅ [authService] Fleet forgot password response:', response);
      return handleApiSuccess(response);
    } catch (error) {
      console.error('❌ [authService] Fleet forgot password error:', error);
      throw handleApiError(error);
    }
  }

  // Fleet Reset Password (POST /v1/fleet/auth/reset-password)
  async fleetResetPassword(resetData) {
    try {
      console.log('🚛 [authService] Fleet reset password request');
      const fleetResetPasswordUrl = getV1EndpointUrl(API_CONFIG.FLEET_ENDPOINTS.RESET_PASSWORD);
      console.log('🚛 [authService] Fleet reset password URL:', fleetResetPasswordUrl);
      const response = await api.post(fleetResetPasswordUrl, resetData);
      console.log('✅ [authService] Fleet reset password response:', response);
      return handleApiSuccess(response);
    } catch (error) {
      console.error('❌ [authService] Fleet reset password error:', error);
      throw handleApiError(error);
    }
  }

  // Shared helper for storing fleet login response data
  storeFleetLoginData(result) {
    // Handle new response structure: data.tokens.accessToken, data.tokens.refreshToken, data.user, data.fleet
    if (result.data?.tokens) {
      // Store access token
      if (result.data.tokens.accessToken) {
        localStorage.setItem(API_CONFIG.STORAGE_KEYS.JWT_TOKEN, result.data.tokens.accessToken);
        console.log('✅ Fleet Access Token stored');
      }

      // Store refresh token
      if (result.data.tokens.refreshToken) {
        localStorage.setItem(API_CONFIG.STORAGE_KEYS.REFRESH_TOKEN, result.data.tokens.refreshToken);
        console.log('✅ Fleet Refresh Token stored');
      }
    }

    // Store user data
    if (result.data?.user) {
      // Elevate city/state to root user object for easy access
      // Check: user.operator, sibling operator, or fleet data
      const operatorData = result.data.user.operator || result.data.operator || result.data.fleet;
      if (operatorData?.city && !result.data.user.city) {
        result.data.user.city = operatorData.city;
      }
      if (operatorData?.state && !result.data.user.state) {
        result.data.user.state = operatorData.state;
      }
      localStorage.setItem(API_CONFIG.STORAGE_KEYS.USER_DATA, JSON.stringify(result.data.user));
      console.log('✅ Fleet User data stored:', result.data.user);
    }

    // Store fleet data
    if (result.data?.fleet) {
      const fleetData = result.data.fleet;
      localStorage.setItem(API_CONFIG.STORAGE_KEYS.FLEET_DATA, JSON.stringify(fleetData));
      console.log('✅ Fleet data stored in localStorage');

      // Store fleet_id separately for easy access
      const fleetId = fleetData.operator_id || fleetData.id || fleetData.fleetId;

      if (fleetId) {
        localStorage.setItem(API_CONFIG.STORAGE_KEYS.FLEET_ID, String(fleetId));
        console.log('✅ Fleet ID stored:', fleetId);

        // Also add fleet_id and city to user data for backward compatibility
        if (result.data?.user) {
          const userData = {
            ...result.data.user,
            operator_id: fleetId,
            city: result.data.user.city || fleetData.city || '',
            state: result.data.user.state || fleetData.state || ''
          };
          localStorage.setItem(API_CONFIG.STORAGE_KEYS.USER_DATA, JSON.stringify(userData));
          console.log('✅ Fleet ID + city added to user data:', fleetId, userData.city);
        }
      } else {
        console.error('❌ [authService] Fleet ID not found in fleet data!');
        console.error('❌ [authService] Fleet data structure:', JSON.stringify(sanitizeForLogging(fleetData), null, 2));
        console.error('❌ [authService] Available keys in fleet data:', Object.keys(fleetData));
      }
    }
  }
  // Get user profile
  async getProfile() {
    try {
      const response = await api.get(getAuthEndpointUrl(API_CONFIG.AUTH_ENDPOINTS.PROFILE));
      return handleApiSuccess(response);
    } catch (error) {
      throw handleApiError(error);
    }
  }
  // Update user profile
  async updateProfile(profileData) {
    try {
      const response = await api.put(getAuthEndpointUrl(API_CONFIG.AUTH_ENDPOINTS.PROFILE), profileData);
      const result = handleApiSuccess(response);
      // Update stored user data
      if (result.data.user) {
        localStorage.setItem(API_CONFIG.STORAGE_KEYS.USER_DATA, JSON.stringify(result.data.user));
      }
      return result;
    } catch (error) {
      throw handleApiError(error);
    }
  }
  // Change password step 1: verify the current password and email a one-time code
  async requestChangePasswordOtp(passwordData) {
    try {
      const response = await api.post(
        getAuthEndpointUrl(API_CONFIG.AUTH_ENDPOINTS.CHANGE_PASSWORD_REQUEST_OTP),
        passwordData
      );
      return handleApiSuccess(response);
    } catch (error) {
      throw handleApiError(error);
    }
  }
  // Change password
  async changePassword(passwordData) {
    try {
      const response = await api.put(
        getAuthEndpointUrl(API_CONFIG.AUTH_ENDPOINTS.CHANGE_PASSWORD),
        passwordData
      );
      return handleApiSuccess(response);
    } catch (error) {
      throw handleApiError(error);
    }
  }

  // First-time password reset (for new users)
  async firstTimePasswordReset(passwordData) {
    try {
      const response = await api.post(
        getAuthEndpointUrl(API_CONFIG.AUTH_ENDPOINTS.FIRST_TIME_PASSWORD_RESET || '/auth/first-time-password-reset'),
        passwordData
      );
      const result = handleApiSuccess(response);
      // Update stored user data to remove first-time login flag
      if (result.data.user) {
        localStorage.setItem(API_CONFIG.STORAGE_KEYS.USER_DATA, JSON.stringify(result.data.user));
      }
      return result;
    } catch (error) {
      throw handleApiError(error);
    }
  }
  // Forgot password
  async forgotPassword(email) {
    try {
      const response = await api.post(
        getAuthEndpointUrl(API_CONFIG.AUTH_ENDPOINTS.FORGOT_PASSWORD),
        { email, type: 'PASSWORD_RESET' }
      );
      return handleApiSuccess(response);
    } catch (error) {
      throw handleApiError(error);
    }
  }
  // Reset password
  async resetPassword(email, otpCode, newPassword) {
    try {
      const response = await api.post(
        getAuthEndpointUrl(API_CONFIG.AUTH_ENDPOINTS.RESET_PASSWORD),
        { contact: email, otpCode, newPassword }
      );
      return handleApiSuccess(response);
    } catch (error) {
      throw handleApiError(error);
    }
  }
  // OTP Services
  async generateOtp(contact, type, userId = null) {
    try {
      const response = await api.post(getOtpEndpointUrl(API_CONFIG.OTP_ENDPOINTS.GENERATE), {
        contact,
        type,
        userId
      });
      return handleApiSuccess(response);
    } catch (error) {
      throw handleApiError(error);
    }
  }
  async verifyOtp(contact, code, type) {
    try {
      const response = await api.post(getOtpEndpointUrl(API_CONFIG.OTP_ENDPOINTS.VERIFY), {
        contact,
        code,
        type
      });
      return handleApiSuccess(response);
    } catch (error) {
      console.log('✅ [authService] Hospital profile response:', sanitizeForLogging(response));
    }
  }
  async resendOtp(contact, type, deliveryMethod = 'sms') {
    try {
      const response = await api.post(getOtpEndpointUrl(API_CONFIG.OTP_ENDPOINTS.RESEND), {
        contact,
        type,
        deliveryMethod
      });
      return handleApiSuccess(response);
    } catch (error) {
      throw handleApiError(error);
    }
  }
  async checkOtpStatus(contact, type, withinMinutes = 30) {
    try {
      const response = await api.post(getOtpEndpointUrl(API_CONFIG.OTP_ENDPOINTS.CHECK), {
        contact,
        type,
        withinMinutes
      });
      return handleApiSuccess(response);
    } catch (error) {
      throw handleApiError(error);
    }
  }
  // Utility methods
  isAuthenticated() {
    const storage = getLocalStorage();
    if (!storage) {
      return false;
    }
    const token = storage.getItem(API_CONFIG.STORAGE_KEYS.JWT_TOKEN);
    const userData = storage.getItem(API_CONFIG.STORAGE_KEYS.USER_DATA);
    return !!(token && userData);
  }

  // Helper method to get user session from cookie (accessible cookie)
  getUserSessionFromCookie() {
    try {
      if (typeof window === 'undefined' || !document || !document.cookie) {
        return null;
      }
      const cookies = document.cookie.split(';');
      const sessionCookie = cookies.find(cookie =>
        cookie.trim().startsWith('user_session=')
      );
      if (sessionCookie) {
        const sessionData = sessionCookie.split('=')[1];
        return JSON.parse(decodeURIComponent(sessionData));
      }
      return null;
    } catch (error) {
      console.warn('Failed to parse user session cookie:', error);
      return null;
    }
  }

  getToken() {
    const storage = getLocalStorage();
    return storage ? storage.getItem(API_CONFIG.STORAGE_KEYS.JWT_TOKEN) : null;
  }
  getUser() {
    try {
      const storage = getLocalStorage();
      if (!storage) {
        return null;
      }

      const userData = storage.getItem(API_CONFIG.STORAGE_KEYS.USER_DATA);
      console.log('🔍 [authService] Raw user data from localStorage:', userData);

      if (!userData) {
        console.log('❌ [authService] No user data found in localStorage');
        return null;
      }

      const parsedData = JSON.parse(userData);
      console.log('🔍 [authService] Parsed user data:', parsedData);
      console.log('🔍 [authService] User role from parsed data:', parsedData?.role);

      return parsedData;
    } catch (error) {
      console.error('❌ [authService] Error parsing user data from localStorage:', error);
      const storage = getLocalStorage();
      if (storage) {
        console.error('❌ [authService] Raw data that failed to parse:', storage.getItem(API_CONFIG.STORAGE_KEYS.USER_DATA));
      }
      return null;
    }
  }
  clearAuth() {
    const storage = getLocalStorage();
    if (!storage) {
      return;
    }
    storage.removeItem(API_CONFIG.STORAGE_KEYS.USER_DATA);
    storage.removeItem(API_CONFIG.STORAGE_KEYS.JWT_TOKEN);
    storage.removeItem(API_CONFIG.STORAGE_KEYS.REFRESH_TOKEN);
  }

  // Add this new function for completing registration after OTP verification:
  async completeRegistration(userData) {
    try {
      const response = await api.post(
        getAuthEndpointUrl(API_CONFIG.ENHANCED_AUTH_ENDPOINTS.COMPLETE_REGISTRATION),
        userData
      );
      return handleApiSuccess(response);
    } catch (error) {
      throw handleApiError(error);
    }
  }

  // Hospital Registration (Super Admin Only)
  async registerHospital(hospitalData) {
    try {
      const response = await api.post('/hospital/register', hospitalData);
      return handleApiSuccess(response);
    } catch (error) {
      throw handleApiError(error);
    }
  }

  // Fleet Operator Registration (Super Admin Only)
  async registerFleetOperator(fleetData) {
    try {
      const response = await api.post('/auth/admin/register-fleet-operator', fleetData);
      return handleApiSuccess(response);
    } catch (error) {
      throw handleApiError(error);
    }
  }

  // Super Admin specific methods
  async getAllFleetOperators(params = {}) {
    try {
      const response = await api.get('/auth/admin/fleet-operators', { params });
      const result = handleApiSuccess(response);
      return {
        ...result,
        meta: response.data?.meta || result.meta,
      };
    } catch (error) {
      throw handleApiError(error);
    }
  }

  // GET /auth/admin/fleet-operators pages at 10 per call by default (100 max
  // — see queryParamsSchema on the backend) — every dropdown/select that
  // called getAllFleetOperators() with no params was silently only ever
  // seeing the first page. handleApiSuccess also strips the response's
  // `meta.pagination` block, so page-walking has to read the raw response
  // here rather than going through it. Use this for anything that needs
  // the FULL list (dropdowns, selects) — keep using getAllFleetOperators
  // directly only for an actual paginated table view (e.g. CompaniesTable).
  async getAllFleetOperatorsUnpaginated() {
    try {
      const limit = 100; // server-enforced max
      let page = 1;
      let totalPages = 1;
      const all = [];
      do {
        const response = await api.get('/auth/admin/fleet-operators', { params: { page, limit } });
        const pageOperators = response.data?.data;
        if (Array.isArray(pageOperators)) all.push(...pageOperators);
        totalPages = response.data?.meta?.pagination?.pages || 1;
        page += 1;
      } while (page <= totalPages);
      return { success: true, data: all };
    } catch (error) {
      throw handleApiError(error);
    }
  }

  async getFleetOperatorById(id) {
    try {
      const response = await api.get(`/auth/admin/fleet-operators/${id}`);
      return handleApiSuccess(response);
    } catch (error) {
      throw handleApiError(error);
    }
  }

  async updateFleetOperator(id, fleetData) {
    try {
      const response = await api.put(`/auth/admin/fleet-operators/${id}`, fleetData);
      return handleApiSuccess(response);
    } catch (error) {
      throw handleApiError(error);
    }
  }

  async deleteFleetOperator(id) {
    try {
      const response = await api.delete(`/auth/admin/fleet-operators/${id}`);
      return handleApiSuccess(response);
    } catch (error) {
      throw handleApiError(error);
    }
  }

  async getAllUsers() {
    try {
      const response = await api.get('/auth/admin/users');
      return handleApiSuccess(response);
    } catch (error) {
      throw handleApiError(error);
    }
  }

  async getUserById(id) {
    try {
      const response = await api.get(`/api/auth/admin/users/${id}`);
      return handleApiSuccess(response);
    } catch (error) {
      throw handleApiError(error);
    }
  }

  async updateUser(id, userData) {
    try {
      const response = await api.put(`/api/auth/admin/users/${id}`, userData);
      return handleApiSuccess(response);
    } catch (error) {
      throw handleApiError(error);
    }
  }

  async deleteUser(id) {
    try {
      const response = await api.delete(`/api/auth/admin/users/${id}`);
      return handleApiSuccess(response);
    } catch (error) {
      throw handleApiError(error);
    }
  }

  async getAllHospitals(params = {}) {
    try {
      // Supports pagination: /api/hospital/all?limit=&page=
      // NOTE: api client already prefixes `/api`, so keep path as `/hospital/all`
      const response = await api.get('/hospital/all', { params });
      const result = handleApiSuccess(response);
      return {
        ...result,
        meta: response.data?.meta || result.meta,
      };
    } catch (error) {
      throw handleApiError(error);
    }
  }

  async getHospitalById(id) {
    try {
      const response = await api.get(`/hospital/profile/${id}`);
      return handleApiSuccess(response);
    } catch (error) {
      throw handleApiError(error);
    }
  }

  async updateHospital(id, hospitalData) {
    try {
      const response = await api.put(`/hospital/profile/${id}`, hospitalData);
      return handleApiSuccess(response);
    } catch (error) {
      throw handleApiError(error);
    }
  }

  async deleteHospital(id) {
    try {
      const response = await api.delete(`/hospital/${id}`);
      return handleApiSuccess(response);
    } catch (error) {
      throw handleApiError(error);
    }
  }

  // Get all companies (hospitals and fleet operators combined)
  async getAllCompanies() {
    try {
      console.log('🔄 Fetching companies data...');

      // Fetch hospitals and fleet operators in parallel
      // Use Promise.allSettled to handle partial failures gracefully
      const [hospitalsResult, fleetResult] = await Promise.allSettled([
        this.getAllHospitals(),
        this.getAllFleetOperatorsUnpaginated()
      ]);

      let hospitals = [];
      let fleetOperators = [];

      // Process hospitals response
      if (hospitalsResult.status === 'fulfilled') {
        const hospitalsResponse = hospitalsResult.value;
        console.log('📊 Hospitals response:', hospitalsResponse);

        if (hospitalsResponse.data && Array.isArray(hospitalsResponse.data)) {
          hospitals = hospitalsResponse.data.map(hospital => ({
            id: hospital.hospital_id,
            name: hospital.name,
            type: "hospital",
            location: hospital.address || "N/A",
            status: (hospital.partnership_status || hospital.status || "active").toLowerCase(),
            users: hospital.user_count || 0,
            requests: hospital.booking_count || 0,
            joinedDate: hospital.createdAt || hospital.created_at
          }));
          console.log('✅ Transformed hospitals:', hospitals.length);
        } else {
          console.warn('⚠️ Invalid hospitals data structure:', hospitalsResponse);
        }
      } else {
        console.error('❌ Failed to fetch hospitals:', hospitalsResult.reason);
      }

      // Process fleet operators response
      if (fleetResult.status === 'fulfilled') {
        const fleetResponse = fleetResult.value;
        console.log('🚛 Fleet operators response:', fleetResponse);

        if (fleetResponse.data && Array.isArray(fleetResponse.data)) {
          fleetOperators = fleetResponse.data.map(fleet => ({
            id: fleet.operator_id,
            name: fleet.name,
            type: "fleet",
            location: fleet.address || `${fleet.city || ''}, ${fleet.state || ''}`.trim() || "N/A",
            status: (fleet.partnership_status || fleet.status || "active").toLowerCase(),
            users: fleet.user_count || 0,
            requests: fleet.booking_count || 0,
            joinedDate: fleet.createdAt || fleet.created_at
          }));
          console.log('✅ Transformed fleet operators:', fleetOperators.length);
        } else {
          console.warn('⚠️ Invalid fleet operators data structure:', fleetResponse);
        }
      } else {
        console.error('❌ Failed to fetch fleet operators:', fleetResult.reason);
      }

      // If both requests failed, throw an error
      if (hospitalsResult.status === 'rejected' && fleetResult.status === 'rejected') {
        const errorMessage = `Failed to fetch companies: ${hospitalsResult.reason?.message || 'Unknown error'}`;
        console.error('❌ Both API calls failed:', errorMessage);
        throw new Error(errorMessage);
      }

      // Combine and return all companies (even if one source failed)
      const allCompanies = [...hospitals, ...fleetOperators];
      console.log('🎯 Total companies:', allCompanies.length);

      // If we have no companies and at least one request failed, show a warning
      if (allCompanies.length === 0 && (hospitalsResult.status === 'rejected' || fleetResult.status === 'rejected')) {
        console.warn('⚠️ No companies found and some requests failed');
      }

      return {
        success: true,
        data: allCompanies
      };
    } catch (error) {
      console.error('❌ Error in getAllCompanies:', error);
      throw handleApiError(error);
    }
  }

  // =========== AMBULANCE TYPES METHODS ===========
  // Get Ambulance Types (GET /api/ambulance/types)
  async getAmbulanceTypes() {
    try {
      console.log('🚑 [authService] Getting ambulance types');
      const ambulanceTypesUrl = getV1EndpointUrl(API_CONFIG.AMBULANCE_ENDPOINTS.TYPES);
      console.log('🚑 [authService] Ambulance types URL:', ambulanceTypesUrl);
      const response = await api.get(ambulanceTypesUrl);
      console.log('✅ [authService] Ambulance types response:', sanitizeForLogging(response));
      return handleApiSuccess(response);
    } catch (error) {
      console.error('❌ [authService] Get ambulance types error:', error);
      throw handleApiError(error);
    }
  }
}
// Create and export singleton instance
const authService = new AuthService();
export default authService;
